import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CartService } from 'app/core/cart/cart.service';
import { ApiService } from 'app/shared/api.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

export interface GuestBookPackage {
    id: string;
    name: string;
    badge?: string;
    badgeColor?: string;
    price: number;
    regularPrice?: number;
    priceFormatted: string;
    description: string;
    image: string;
    features: string[];
    hardware: string[];
    crew: string;
}

interface ApiNonThemedPackage {
    id: string;
    non_themed_product_id: string;
    non_themed_product_name: string;
    name: string;
    description: string | null;
    regular_price: number;
    price: number;
    is_active: boolean;
    product_package_type: string;
    devices: any[];
}

export interface GuestBookAddon {
    id: string;
    name: string;
    price: number;
    priceFormatted: string;
    description: string;
    selected: boolean;
}

@Component({
    selector: 'app-guest-book',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './guest-book.component.html',
    styleUrls: ['./guest-book.component.scss'],
})
export class GuestBookComponent implements OnInit {
    isLoading: boolean = false;
    selectedPackageId: string = '';
    packages: GuestBookPackage[] = [];

    addons: GuestBookAddon[] = [
        {
            id: 'extra-ipad',
            name: 'Extra iPad Station & Stand',
            price: 350000,
            priceFormatted: 'Rp 350.000',
            description: 'Tambahan 1 unit iPad & standing bracket untuk jalur meja registrasi ekstra.',
            selected: false
        },
        {
            id: 'extra-crew',
            name: 'Extra Crew Support Onsite',
            price: 250000,
            priceFormatted: 'Rp 250.000',
            description: 'Tambahan 1 orang tim operator registrasi tamu di lokasi acara.',
            selected: false
        },
        {
            id: 'wa-blast',
            name: 'WhatsApp Blast E-Ticket Gateway',
            price: 200000,
            priceFormatted: 'Rp 200.000',
            description: 'Kirim link QR Code e-ticket dan ucapan pengingat otomatis ke WhatsApp para tamu.',
            selected: false
        },
        {
            id: 'print-card',
            name: 'Cetak Kartu QR Fisik Tamu (100 pcs)',
            price: 150000,
            priceFormatted: 'Rp 150.000',
            description: 'Kartu QR eksklusif fisik untuk tamu khusus / VIP.',
            selected: false
        }
    ];

    // Form model
    customerName: string = '';
    customerPhone: string = '';
    customerEmail: string = '';
    eventName: string = '';
    eventType: string = 'Wedding';
    eventDate: string = '';
    eventCity: string = '';
    eventVenue: string = '';
    estimatedGuests: string = '100-300';
    notes: string = '';

    formSubmitted: boolean = false;
    isSubmitting: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private cartService: CartService,
        private apiService: ApiService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.fetchGuestBookPackages();

        this.route.params.subscribe((params) => {
            const pkg = String(params['package'] || '').toLowerCase().trim();
            if (pkg && this.packages.length > 0) {
                const found = this.packages.find(p => p.name.toLowerCase().includes(pkg));
                if (found) {
                    this.selectedPackageId = found.id;
                }
            }
        });

        // Scroll to top on init
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }

    async fetchGuestBookPackages(): Promise<void> {
        this.isLoading = true;

        try {
            const apiEndpoint = '/api/v1/front/public/non-themed-product-packages/list?paginate=false';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Fallback fetch error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list) && response.data.list.length > 0) {
                const guestBookPackages = response.data.list.filter((pkg: ApiNonThemedPackage) => 
                    pkg.non_themed_product_name.toLowerCase().includes('guest book')
                );

                if (guestBookPackages.length > 0) {
                    this.packages = this.transformGuestBookPackages(guestBookPackages);
                    if (this.packages.length > 0 && !this.selectedPackageId) {
                        this.selectedPackageId = this.packages[0].id;
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching guest book packages:', error);
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private transformGuestBookPackages(apiList: ApiNonThemedPackage[]): GuestBookPackage[] {
        return apiList.map((item) => {
            return {
                id: item.id,
                name: item.name,
                badge: '',
                badgeColor: '',
                price: item.price,
                regularPrice: item.regular_price,
                priceFormatted: this.cartService.formatCurrency(item.price),
                description: item.description || '',
                image: '',
                features: this.getBenefits(item),
                hardware: [],
                crew: ''
            };
        });
    }

    private getBenefits(pkg: any): string[] {
        // Try multiple possible benefit sources
        const benefitData = 
            pkg?.properties?.benefit || 
            pkg?.benefit || 
            pkg?.benefits || 
            pkg?.features ||
            pkg?.description;

        if (!benefitData) {
            return [];
        }

        // If it's already an array, return it
        if (Array.isArray(benefitData)) {
            return benefitData.filter(b => b && typeof b === 'string');
        }

        // If it's a string, try parsing as JSON first
        if (typeof benefitData === 'string') {
            try {
                const parsed = JSON.parse(benefitData);
                if (Array.isArray(parsed)) {
                    return parsed.filter(b => b && typeof b === 'string');
                }
            } catch (e) {
                // Not JSON, try splitting by newlines
                const lines = benefitData
                    .split(/\r?\n/)
                    .map(line => line.trim())
                    .filter(line => line.length > 0);
                
                if (lines.length > 0) {
                    return lines;
                }
            }
        }

        return [];
    }

    selectPackage(pkgId: string): void {
        this.selectedPackageId = pkgId;
    }

    getSelectedPackage(): GuestBookPackage | undefined {
        return this.packages.find(p => p.id === this.selectedPackageId) || this.packages[0];
    }

    toggleAddon(addon: GuestBookAddon): void {
        addon.selected = !addon.selected;
    }

    calculateAddonsTotal(): number {
        return this.addons
            .filter((a) => a.selected)
            .reduce((sum, a) => sum + a.price, 0);
    }

    getSelectedAddonsCount(): number {
        return this.addons.filter((addon) => addon.selected).length;
    }

    calculateGrandTotal(): number {
        const pkg = this.getSelectedPackage();
        return (pkg?.price || 0) + this.calculateAddonsTotal();
    }

    onPhoneInput(event: any): void {
        const val = event.target.value || '';
        const cleaned = val.replace(/[^0-9]/g, '');
        this.customerPhone = cleaned;
        if (event.target.value !== cleaned) {
            event.target.value = cleaned;
        }
    }

    isFormValid(): boolean {
        return !!(
            this.customerName.trim() &&
            this.customerPhone.trim() &&
            this.eventName.trim() &&
            this.eventDate.trim() &&
            this.eventVenue.trim()
        );
    }

    submitOrder(): void {
        this.formSubmitted = true;

        if (!this.isFormValid()) {
            // Scroll smoothly to form
            const formElem = document.getElementById('booking-form');
            if (formElem) {
                formElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        const pkg = this.getSelectedPackage();
        const selectedAddons = this.addons.filter((a) => a.selected);
        const grandTotal = this.calculateGrandTotal();

        const selectedOption = selectedAddons.length
            ? `Add-ons: ${selectedAddons.map((addon) => addon.name).join(', ')}`
            : 'Tanpa add-on';
        const description = [
            `Nama Pemesan: ${this.customerName.trim()}`,
            `No. WhatsApp: ${this.customerPhone.trim()}`,
            this.customerEmail.trim() ? `Email: ${this.customerEmail.trim()}` : '',
            `Nama Acara: ${this.eventName.trim()}`,
            `Jenis Acara: ${this.eventType}`,
            `Tanggal Acara: ${this.eventDate}`,
            this.eventCity.trim() ? `Kota: ${this.eventCity.trim()}` : '',
            `Lokasi/Venue: ${this.eventVenue.trim()}`,
            `Estimasi Tamu: ${this.estimatedGuests} Tamu`,
            this.notes.trim() ? `Catatan: ${this.notes.trim()}` : '',
        ].filter(Boolean).join('\n');
        const existingItem = this.cartService.items.find((item) =>
            item.category === 'Guest Book' && item.name === pkg.name &&
            item.price === grandTotal && item.selectedOption === selectedOption &&
            item.description === description
        );

        this.cartService.addItem({
            id: existingItem?.id || `guestbook_${pkg.id}_${crypto.randomUUID()}`,
            name: pkg.name,
            category: 'Guest Book',
            price: grandTotal,
            priceFormatted: this.cartService.formatCurrency(grandTotal),
            image: pkg.image,
            description,
            selectedOption,
            selectedAddons: selectedAddons.map(({ name, price }) => ({ name, price })),
        }, 1);
        this.cartService.openCart();
    }

    goBack(): void {
        this.router.navigate(['/']);
    }
}
